import React from 'react';
import PropTypes from 'prop-types';
import { Upload, Input, Icon, Button, Alert, Radio, Row, Col, Select, InputNumber, Modal } from 'antd';
import FormItem from './FormItem';
import { validate } from '../../utils/validator';
import './BaseForm.css';
import classNames from "classnames";

const { TextArea } = Input;

class BaseForm extends React.Component {
  static propTypes = {
    onSubmit: PropTypes.func,
  };

  static defaultProps = {
    onSubmit(data) {
      // eslint-disable-next-line no-console
      console.log('Data to submit', data);
    },
  };

  state = {
    data: {},
    errors: {},
    fileList: {},
  };

  // eslint-disable-next-line
  styles = {
    btnSubmit: { marginRight: 10 },
    alertError: { minWidth: 'min-content' },
  };

  goBack = this.goBack.bind(this);

  onChange = (e) => {
    const { name, value } = e.target;
    this.updateData(name, value);
  };

  onInputNumberChange = (name) => {
    return (value) => {
      if (typeof value === 'string') {
        const match = value.match(/\d+\.{1}\d*/g);
        value = match ? match.join('') : undefined;
      }

      this.updateData(name, value);
    };
  };

  onValueChange = (name) => {
    return (value) => {
      this.updateData(name, value);
      return false; // need for upload element
    };
  };

  updateData = (name, value) => {
    let { data, errors } = this.state; // eslint-disable-line prefer-const

    data[name] = value;

    const validation = validate(this.validationSchema, data);

    this.setState({ data, errors });
  };

  onSubmit = (e) => {
    e.preventDefault();

    const validation = validate(this.validationSchema, this.state.data);


    if (Object.keys(validation.errors).length > 0) {
      this.setState({ errors: validation.errors });
    } else {
      this.props.onSubmit(validation.data);
    }
  };

  goBack() {
    this.props.history.goBack();
  }

  renderUpload(name, accept, required, label, multiple, layout, className) {
    const { data } = this.state;
    const { fileList } = this.state;

    if (data[name]) {
      if (!fileList.hasOwnProperty(name) || (typeof data[name] !== 'string' && data[name] !== fileList[name])) {
        if (data[name] instanceof File) {
          const file = data[name];
          const reader = new FileReader();

          reader.onloadend = () => {
            file.url = reader.result;
            fileList[name] = file;
            this.setState({ fileList });
          };

          reader.readAsDataURL(file);
        } else {
          fileList[name] = {
            uid: -1,
            name,
            status: 'done',
            url: `${data[name]}?${Date.now()}`,
          };

          this.setState({ fileList });
        }
      }
    }

    const files = fileList[name] ? [fileList[name]] : [];

    return (
      <FormItem
        label={label}
        required={required}
        layout={layout}
      >
        <Upload
          className={classNames('uploadForm', className)}
          listType="picture-card"
          accept={accept}
          multiple={multiple}
          beforeUpload={this.onValueChange(name)}
          fileList={files}
          onPreview={this.handleImagePreview}
          onRemove={this.handleImageRemove}
        >
          <Button>
            <Icon type="upload" />
            Select file
          </Button>
        </Upload>
        <Modal visible={this.state.previewVisible} footer={null} onCancel={this.handleCancelPreview}>
          <img alt="example" style={{ width: '100%' }} src={this.state.previewImage} />
        </Modal>
      </FormItem>
    );
  }

  handleCancelPreview = () => this.setState({ previewVisible: false });

  handleImagePreview = async file => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    this.setState({
      previewImage: file.url || file.preview,
      previewVisible: true,
    });
  };

  handleImageRemove = () => {
    const data = this.state.data;
    data.logo = null;
    data.delete_logo = true;
    this.setState({data, fileList: {}});
  }

  renderInput(name, required, label, placeholder, type = 'text', tabIndex, layout) {
    const { data, errors } = this.state;

    return (
      <FormItem
        label={label}
        error={errors[name]}
        required={required}
        layout={layout}
      >
        <Input
          tabIndex={tabIndex}
          type={type}
          name={name}
          placeholder={placeholder}
          value={data[name]}
          onChange={this.onChange}
        />
      </FormItem>
    );
  }

  renderInputNumber(name, label, required, layout, options) {
    const { data, errors } = this.state;

    return (
      <FormItem
        label={label}
        error={errors[name]}
        required={required}
        layout={layout}
      >
        <InputNumber
          name={name}
          value={data[name]}
          onChange={this.onInputNumberChange(name)}
          {...options}
        />
      </FormItem>
    );
  }

  renderTextArea(name, label, required, options) {
    const { data, errors } = this.state;

    return (
      <FormItem
        label={label}
        error={errors[name]}
        required={required}
        layout={options.layout}
      >
        <TextArea
          name={name}
          value={data[name]}
          onChange={this.onChange}
          {...options}
        />
      </FormItem>
    );
  }

  renderRadio(label, name, options, required) {
    const { data, errors } = this.state;

    return (
      <FormItem
        label={label}
        required={required}
        error={errors[name]}
      >
        <Radio.Group
          name={name}
          value={data[name]}
          onChange={this.onChange}
        >
          {options}
        </Radio.Group>
      </FormItem>
    );
  }

  renderSelect(name, label, required, options, extraOptions) {
    const { data, errors } = this.state;

    if (extraOptions && extraOptions.showSearch) {
      extraOptions.filterOption = (input, option) => (
        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
      );
    }

    return (
      <FormItem
        label={label}
        required={required}
        error={errors[name]}
        layout={extraOptions.layout}
      >
        <Select
          name={name}
          value={data[name]}
          onChange={this.onValueChange(name)}
          {...extraOptions}
        >
          {options}
        </Select>
      </FormItem>
    );
  }

  renderComponent(name, label, required, Component, options = {}) {
    const { data, errors } = this.state;

    return (
      <FormItem
        label={label}
        required={required}
        error={errors[name]}
      >
        <Component
          value={data[name]}
          onChange={this.onValueChange(name)}
          {...options.props}
        />
      </FormItem>
    );
  }

  renderSubmitButton(label, loading) {
    return (
      <Button style={this.styles.btnSubmit}
              type="primary"
              loading={loading}
              htmlType="submit">{label}</Button>
    );
  }

  renderFormActions(submitLabel, loading) {
    return (
      <FormItem layout={{ wrapperCol: { span: 24 } }}>
        <Col xs={4} lg={4}>
          <Button className="buttonSubmit"
                  type="primary"
                  onClick={this.goBack}
                  disabled={loading}>
            <Icon type="arrow-left" />
            Back
          </Button>
        </Col>
        <Col xs={20} lg={20}>
          <Row type="flex" justify="end">
            <Col>
              {this.renderSubmitButton(submitLabel, loading)}
            </Col>
            <Col>
              <Button className="buttonSubmit"
                      type="primary"
                      onClick={this.goBack}
                      disabled={loading}>Cancel</Button>
            </Col>
          </Row>
        </Col>

      </FormItem>
    );
  }

  renderSuccessAlert(text, onClose) {
    const closable = Boolean(onClose);
    const layout = {
      labelCol: { span: 0 },
      wrapperCol: { span: 24 },
    };

    return (
      <FormItem layout={layout}>
        <Alert showIcon
               type="success"
               message="Success"
               description={text}
               closable={closable}
               onClose={onClose} />
      </FormItem>
    );
  }

  // todo change to Error Alert
  renderErrorAlert(text) {
    const layout = {
      labelCol: { span: 0 },
      wrapperCol: { span: 24 },
    };

    return (
      <FormItem layout={layout}>
        <Alert style={this.styles.alertError}
               showIcon
               closable
               type="error"
               message="Error"
               description={text} />
      </FormItem>
    );
  }
}

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

export default BaseForm;



// WEBPACK FOOTER //
// ./src/components/BaseForm/index.js